import { get } from 'lodash';
import { Localization } from '../Localization';
import { translate, TranslateOptions } from './translate';

export type LocalizationContent<
  TLocalization extends Localization,
  TKey extends keyof TLocalization,
> = TLocalization[TKey];

export type TranslateContent<TLocalization extends Localization> = <
  TKey extends keyof TLocalization,
>(
  key: TKey,
  options?: TranslateOptions | null | undefined,
) => LocalizationContent<TLocalization, TKey>;
// NOTE: can be returned by hook in the future where we want to switch languages

export function createTranslateContent<TLocalization extends Localization>(
  localization: TLocalization,
): <TKey extends keyof TLocalization>(
  key: TKey,
  options?: TranslateOptions | null | undefined,
) => LocalizationContent<TLocalization, TKey> {
  return (key, options) => translateKey(localization, key, options);
}

// Recursive function for handling nested translations
function translateKey<
  TLocalization extends Localization,
  TranslationPath extends keyof TLocalization,
>(
  localization: TLocalization,
  path: TranslationPath,
  options?: TranslateOptions | null | undefined,
): any {
  const property = get(localization, path);
  const type = typeof property;
  const stringPath = String(path);
  if (Array.isArray(property)) {
    return (property as Array<any>).map((item: any, index) =>
      translateKey(localization, `${stringPath}.${index}`, options),
    );
  }
  // Test if type is object => transform input object to object of translations
  else if (type === 'object') {
    const keys = Object.keys(property);
    return keys.reduce(
      (accumulator, currentKey) => ({
        ...accumulator,
        [currentKey]: translateKey(
          localization,
          `${stringPath}.${currentKey}`,
          options,
        ),
      }),
      {},
    );
    // Else just return the translation string (most nested)
  } else {
    return translate(stringPath, options, localization);
  }
}
